import React, { ReactElement, useContext } from 'react'

import { LanguageContext } from '../../../Common/Contexts'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { Select, SelectOptions } from '../../../Common/Components/Selects'
import FormRow from '../../../Common/Components/FormRow'
import {
    ChartStyleOptions as ChartStyleOptionsLanguage,
    ChartProviderOptions as ChartProviderOptionsLanguage,
    DisplayOptions
} from '../../../../../i18n'

import {
    CHART_STYLE_OPTION_VALUE_PIE_2D,
    CHART_STYLE_OPTION_VALUE_PIE_3D,
    CHART_STYLE_OPTION_VALUE_DOUGHNUT_2D,
    CHART_STYLE_OPTION_VALUE_DOUGHNUT_3D,
    CHART_STYLE_OPTION_VALUE_LINE,
    CHART_STYLE_OPTION_VALUE_AREA,
    CHART_STYLE_OPTION_VALUE_AREA_STACKED,
    CHART_STYLE_OPTION_VALUE_RADAR_POLAR_AREA,
    CHART_STYLE_OPTION_VALUE_COLUMN_2D,
    CHART_STYLE_OPTION_VALUE_COLUMN_2D_STACKED,
    CHART_STYLE_OPTION_VALUE_COLUMN_3D,
    CHART_STYLE_OPTION_VALUE_COLUMN_3D_STACKED,
    CHART_STYLE_OPTION_VALUE_BAR_2D,
    CHART_STYLE_OPTION_VALUE_BAR_2D_STACKED,
    CHART_STYLE_OPTION_VALUE_BAR_3D,
    CHART_STYLE_OPTION_VALUE_BAR_3D_STACKED,
    CHART_PROVIDER_OPTION_VALUE_HIGH_CHARTS,
    CHART_PROVIDER_OPTION_VALUE_AM_CHARTS
} from '../Constants'

/**
 * Component properties type.
 */
type Props = {
    chartType: number
    setChartType: (value: number) => void
    chartProvider: number
    setChartProvider: (value: number) => void
}

/**
 * Chart fields in report tab.
 *
 */
const ChartFields: React.FC<Props> = ({
    chartType,
    setChartType,
    chartProvider,
    setChartProvider
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('ChartStyleOptions')
    language.loadBundle('ChartProviderOptions')

    const chartStyleOptions: SelectOptions<number> = [
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsPie2d)}`,
            value: CHART_STYLE_OPTION_VALUE_PIE_2D
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsPie3d)}`,
            value: CHART_STYLE_OPTION_VALUE_PIE_3D
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsDoughnut2d)}`,
            value: CHART_STYLE_OPTION_VALUE_DOUGHNUT_2D
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsDoughnut3d)}`,
            value: CHART_STYLE_OPTION_VALUE_DOUGHNUT_3D
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsLine)}`,
            value: CHART_STYLE_OPTION_VALUE_LINE
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsArea)}`,
            value: CHART_STYLE_OPTION_VALUE_AREA
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsAreastacked)}`,
            value: CHART_STYLE_OPTION_VALUE_AREA_STACKED
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsRadarpolararea)}`,
            value: CHART_STYLE_OPTION_VALUE_RADAR_POLAR_AREA
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsColumn2d)}`,
            value: CHART_STYLE_OPTION_VALUE_COLUMN_2D
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsColumn2dstacked)}`,
            value: CHART_STYLE_OPTION_VALUE_COLUMN_2D_STACKED
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsColumn3d)}`,
            value: CHART_STYLE_OPTION_VALUE_COLUMN_3D
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsColumn3dstacked)}`,
            value: CHART_STYLE_OPTION_VALUE_COLUMN_3D_STACKED
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsBar2d)}`,
            value: CHART_STYLE_OPTION_VALUE_BAR_2D
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsBar2dstacked)}`,
            value: CHART_STYLE_OPTION_VALUE_BAR_2D_STACKED
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsBar3d)}`,
            value: CHART_STYLE_OPTION_VALUE_BAR_3D
        },
        {
            label: `${language?.get(ChartStyleOptionsLanguage.ChartstyleoptionsBar3dstacked)}`,
            value: CHART_STYLE_OPTION_VALUE_BAR_3D_STACKED
        }
    ]

    const chartProviderOptions: SelectOptions<number> = [
        {
            label: `${language?.get(ChartProviderOptionsLanguage.ChartprovideroptionsHighcharts)}`,
            value: CHART_PROVIDER_OPTION_VALUE_HIGH_CHARTS
        },
        {
            label: `${language?.get(ChartProviderOptionsLanguage.ChartprovideroptionsAmcharts)}`,
            value: CHART_PROVIDER_OPTION_VALUE_AM_CHARTS
        }
    ]

    return (
        <FormRow isInline className={'chart-fields'}>
            <Select
                classNames={'chart-style'}
                label={language?.get(DisplayOptions.ChartStyle)}
                options={chartStyleOptions}
                value={chartType}
                handleChange={setChartType}
            />
            <Select
                classNames={'chart-provider'}
                label={language?.get(DisplayOptions.ChartProvider)}
                options={chartProviderOptions}
                value={chartProvider}
                handleChange={setChartProvider}
            />
        </FormRow>
    )
}

export default ChartFields
